import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { Search, Filter, Calendar, Target, Users, BarChart3 } from 'lucide-preact'
import { campaignApi } from '../api/campaign'
import type { CampaignViewDto, CampaignFilters, CampaignStatus } from '../types/campaign'
import { BRAND_CLASSES } from '../constants/theme'
import { CampaignModal } from './campaigns/CampaignModal'
import { useUserProfile } from '@/hooks/useUserProfile'
import { routes } from '@/utils/routes'
import { lowerSafe } from '@/utils/strings'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatViewModes } from '@/constants/aiChat'

interface CampaignsProps {
  selectedWorkspaceId: string
  clientCompanyId: string
}

// Campaign status colors and icons
const CAMPAIGN_STATUS_CONFIG = {
  Planned: {
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
    icon: Calendar
  },
  Active: {
    color: 'bg-primary-100 text-primary-800 dark:bg-primary-900/20 dark:text-primary-400',
    icon: Target
  },
  Completed: {
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400',
    icon: BarChart3
  },
  Cancelled: {
    color: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    icon: Calendar
  }
} as const

// Campaign status badge component
function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  const config = CAMPAIGN_STATUS_CONFIG[status as keyof typeof CAMPAIGN_STATUS_CONFIG]
  const IconComponent = config.icon

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
      <IconComponent className="w-3 h-3" />
      {status}
    </span>
  )
}

// Campaign card component
function CampaignCard({ campaign, onViewDetails }: { campaign: CampaignViewDto; onViewDetails: (campaign: CampaignViewDto) => void }) {
  const startDate = campaign.StartDate ? new Date(campaign.StartDate).toLocaleDateString() : '—'
  // Treat null/undefined/empty or very early sentinel dates as no end date (ongoing)
  let endDateDisplay: string
  if (!campaign.EndDate) {
    endDateDisplay = 'Ongoing'
  } else {
    const end = new Date(campaign.EndDate)
    // If invalid or clearly a min sentinel (year <= 1971), treat as ongoing
    if (isNaN(end.getTime()) || end.getFullYear() <= 1971) {
      endDateDisplay = 'Ongoing'
    } else {
      endDateDisplay = end.toLocaleDateString()
    }
  }
  // Use backend-provided LeadCount (nullable) with a fallback to 0
  const leadCount = campaign.LeadCount ?? 0

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onViewDetails(campaign)}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onViewDetails(campaign) } }}
      className="flex flex-col h-full bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-primary-300 dark:hover:border-primary-600 p-6 hover:shadow-lg transition-all duration-200 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500"
    >
      {/* Campaign header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {campaign.Name}
          </h3>
          <CampaignStatusBadge status={campaign.Status} />
        </div>
      </div>

      {/* Campaign description */}
      <p className="text-slate-600 dark:text-slate-300 text-sm mb-4 overflow-hidden" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
        {campaign.Description}
      </p>

      {/* Campaign dates */}
      <div className="flex items-center gap-4 text-sm text-slate-500 dark:text-slate-400 mb-4">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" />
          {endDateDisplay === 'Ongoing' ? (
            <span>{startDate} – Ongoing</span>
          ) : (
            <span>{startDate} – {endDateDisplay}</span>
          )}
        </div>
      </div>

      {/* Campaign stats */}
      <div className="mt-auto flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center gap-1 text-sm">
          <Users className="w-4 h-4 text-slate-400" />
          <span className="text-slate-600 dark:text-slate-300">
            <span className="font-medium text-slate-900 dark:text-white">{leadCount}</span> leads
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); onViewDetails(campaign) }}
            className={`text-sm font-medium transition-colors ${BRAND_CLASSES.TEXT.PRIMARY} hover:${BRAND_CLASSES.TEXT.PRIMARY_STRONG}`}
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  )
}

export function Campaigns({ selectedWorkspaceId, clientCompanyId }: CampaignsProps) {
  const location = useLocation()
  const { getWorkspaceCapabilities } = useUserProfile()
  const { canEdit } = getWorkspaceCapabilities(clientCompanyId, selectedWorkspaceId)
  const [campaigns, setCampaigns] = useState<CampaignViewDto[]>([])
  const [filteredCampaigns, setFilteredCampaigns] = useState<CampaignViewDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<CampaignFilters>({
    status: 'all',
    search: ''
  })
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const { updateContext } = useAIChatContextUpdater()

  // Load campaigns
  useEffect(() => {
    loadCampaigns()
  }, [selectedWorkspaceId])

  // Filter campaigns
  useEffect(() => {
    let filtered = campaigns

    // Filter by status
    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(campaign => campaign.Status === filters.status)
    }

    // Filter by search
    if (filters.search?.trim()) {
      const query = filters.search.trim().toLowerCase()
      filtered = filtered.filter(campaign =>
        lowerSafe(campaign.Name).includes(query) ||
        lowerSafe(campaign.Description).includes(query)
      )
    }

    setFilteredCampaigns(filtered)
  }, [campaigns, filters])

  const loadCampaigns = async () => {
    if (!selectedWorkspaceId) return

    setLoading(true)
    setError(null)

    try {
      const response = await campaignApi.getCampaignsByWorkspace(selectedWorkspaceId)

      if (response.error) {
        setError(response.error)
      } else {
        setCampaigns(response.data || [])
      }
    } catch (err) {
      console.error('Error loading campaigns:', err)
      setError('Failed to load campaigns')
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (newFilters: Partial<CampaignFilters>) => {
    setFilters((prev: CampaignFilters) => ({ ...prev, ...newFilters }))
  }

  const handleCampaignSaved = (savedCampaign: CampaignViewDto) => {
    // Add new campaign to the beginning of the list
    setCampaigns(prev => [savedCampaign, ...prev])
  }

  const handleViewDetails = (campaign: CampaignViewDto) => {
    // Navigate to campaign detail page
    // Extract company ID from the current location path
    const pathParts = location.url.split('/')
    const companyIndex = pathParts.indexOf('company')
    const workspaceIndex = pathParts.indexOf('workspace')

    if (companyIndex !== -1 && workspaceIndex !== -1) {
      const companyId = pathParts[companyIndex + 1]
      const workspaceId = pathParts[workspaceIndex + 1]
      location.route(routes.scoped(companyId, workspaceId).campaign(campaign.Id))
    }
  }

  const handleCloseModal = () => {
    setIsCreateModalOpen(false)
    // Reset AI context back to View mode (no editable fields on listing page)
    updateContext({
      location: ChatLocations.CampaignDetails,
      viewMode: ChatViewModes.View,
      availableFields: [],
      availableActions: [],
    })
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4 animate-pulse" />
        <div className="text-slate-600 dark:text-slate-300">Loading campaigns...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <Target className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
        <button
          onClick={loadCampaigns}
          className={`px-4 py-2 rounded-lg transition-colors ${BRAND_CLASSES.BUTTON.PRIMARY}`}
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-slate-900 dark:text-white mb-8">
        Campaigns
      </h2>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-600 dark:text-slate-300">
              Manage and track your marketing campaigns
            </p>
          </div>

          {canEdit && (
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="group relative inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-700 to-primary-800 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
              <Target className="w-4 h-4 relative z-10" />
              <span className="relative z-10">New Campaign</span>
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange({ search: e.currentTarget.value })}
                  className={`w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} focus:border-transparent`}
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-400" />
              <select
                value={filters.status || 'all'}
                onChange={(e) => handleFilterChange({ status: e.currentTarget.value as CampaignStatus | 'all' })}
                className={`px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 ${BRAND_CLASSES.FOCUS.PRIMARY} focus:border-transparent`}
              >
                <option value="all">All Status</option>
                <option value="Planned">Planned</option>
                <option value="Active">Active</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        {/* Campaign Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Object.entries(CAMPAIGN_STATUS_CONFIG).map(([status, config]) => {
            const count = campaigns.filter(c => c.Status === status).length
            const IconComponent = config.icon
            const iconColor = status === 'Active' ? 'text-primary-600 dark:text-primary-400' : 'text-slate-400'

            return (
              <div key={status} className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300">{status}</p>
                    <p className="text-2xl font-bold text-slate-900 dark:text-white">{count}</p>
                  </div>
                  <IconComponent className={`w-8 h-8 ${iconColor}`} />
                </div>
              </div>
            )
          })}
        </div>

        {/* Campaigns Grid */}
        {filteredCampaigns.length === 0 ? (
          <div className="text-center py-12">
            <Target className="h-12 w-12 text-slate-400 dark:text-slate-500 mx-auto mb-4" />
            <div className="text-slate-600 dark:text-slate-300 mb-2">
              {campaigns.length === 0 ? 'No campaigns found' : 'No campaigns match your filters'}
            </div>
            {campaigns.length === 0 && (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Create your first campaign to get started
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign) => (
              <CampaignCard
                key={campaign.Id}
                campaign={campaign}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        )}
      </div>

      {/* Campaign Modal - Only for creating new campaigns */}
      <CampaignModal
        isOpen={isCreateModalOpen}
        onClose={handleCloseModal}
        selectedWorkspaceId={selectedWorkspaceId}
        onCampaignSaved={handleCampaignSaved}
        clientCompanyId={clientCompanyId}
      />
    </div>
  )
}
